import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Chip, Avatar } from '@mui/material';
import { Controller } from 'react-hook-form';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import * as yup from 'yup';
import { FieldError } from 'components/common/form/FieldError';

const emailValidationSchema = yup.string().email('Invalid email');

export const RecipientField = (props) => {
  const {
    id = 'recipient-field',
    name = 'recipient-field',
    type = 'text',
    label = '',
    size = 'small',
    placeholder = '',
    disabled = false,
    control = () => {},
    options = () => [],
    hasError = false,
    error = '',
  } = props;

  const [inputValue, setInputValue] = useState('');

  const isValidEmail = (email) => {
    try {
      emailValidationSchema.validateSync(email);
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleAddEmail = (email, currentList, onChange) => {
    const trimmed = email.trim();
    if (!trimmed || currentList.includes(trimmed)) return;

    const isValid = isValidEmail(trimmed);
    if (isValid) {
      onChange([...currentList, trimmed]);
    }
  };

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            multiple
            id={id}
            options={options}
            freeSolo
            disabled={disabled}
            value={value}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
              }
            }}
            onChange={(event, newValue) => {
              onChange(newValue);
            }}
            onKeyDown={(event) => {
              if (['Enter', ' '].includes(event.key)) {
                event.preventDefault();
                handleAddEmail(inputValue, value, onChange);
                setInputValue('');
              }
            }}
            onBlur={() => {
              handleAddEmail(inputValue, value, onChange);
              setInputValue('');
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                const isInvalidEmail = !isValidEmail(option);

                return (
                  <Chip
                    icon={
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.dark' }}>
                        <EmailOutlinedIcon
                          fontSize="small"
                          sx={{ color: '#ffffff', width: '0.835em', height: '0.835em' }}
                        />
                      </Avatar>
                    }
                    deleteIcon={
                      <CloseOutlinedIcon
                        fontSize="small"
                        sx={{
                          width: '0.835em',
                          height: '0.835em',
                          '&.MuiSvgIcon-root': {
                            color: 'secondary.dark',
                          },
                        }}
                      />
                    }
                    label={option}
                    key={key}
                    {...tagProps}
                    sx={{
                      bgcolor: 'secondary.light',
                      borderColor: isInvalidEmail ? 'red' : undefined,
                      color: isInvalidEmail ? 'red' : undefined,
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size={size}
                label={label}
                placeholder={placeholder}
                type={type}
                error={hasError}
              />
            )}
          />
        )}
      />
      {error && <FieldError message={error} />}
    </>
  );
};

RecipientField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  control: PropTypes.object.isRequired,
  options: PropTypes.array,
  hasError: PropTypes.bool,
  error: PropTypes.string,
};
